import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

import { IWF } from '../iwf/iwfConfig';
import { DemoCtaLink } from '../DemoCtaLink';

/**
 * LaunchHero — the /launch announcement hero: copy on the left, an
 * auto-cycling product reel framed as a browser window on the right.
 *
 * Framed as a *window* rather than a laptop or monitor on purpose. A device
 * has an expected aspect ratio, so a capture that doesn't match it can only be
 * letterboxed or cropped; a window has no expected shape, so the frame is
 * sized to the capture instead of the other way round.
 *
 * Both captures come from public/screenshots — already-shipped assets with
 * demo/test data. Do not swap in the Drive-sourced full-screen captures: those
 * contain live PO numbers and customer names.
 */

// Reel slides. Both captures are ~1:1, but neither uses its bottom quarter for
// anything: the catalog runs out after the last product row and the production
// table trails into empty sidebar. Shown at full height inside a ~1:1 frame that
// dead space read as a layout bug, so FRAME_ASPECT below crops it off from the
// bottom via object-cover/object-top rather than letterboxing it.
const SLIDES = [
  {
    src: '/screenshots/production-tracking.png',
    width: 2000,
    height: 2008,
    label: 'Production tracking — every order, every station',
    alt: 'TimberCloud production dashboard tracking orders across seven stations',
  },
  {
    src: '/screenshots/catalog-management.png',
    width: 2000,
    height: 2050,
    label: 'Catalog — configurable products, priced by formula',
    alt: 'TimberCloud product catalog showing configurable made-to-order products',
  },
];

const CYCLE_MS = 4000;

// One frame aspect for both slides — the reel cross-fades in place, so a
// per-slide aspect would make the frame jump mid-cycle. 1000/794 lands the
// catalog crop just under its last product row; the production table gets cut
// mid-rows, which reads as a table that continues rather than one that ran out.
const FRAME_ASPECT = 'aspect-[1000/794]';

// Static chrome for the window frame — hoisted so it isn't rebuilt per render.
const WINDOW_CHROME = (
  <div className='flex items-center gap-2 border-b border-white/10 bg-onyx/80 px-4 py-3'>
    <span className='flex gap-1.5' aria-hidden='true'>
      <span className='h-3 w-3 rounded-full bg-[#ffffff]/20' />
      <span className='h-3 w-3 rounded-full bg-[#ffffff]/20' />
      <span className='h-3 w-3 rounded-full bg-[#ffffff]/20' />
    </span>
    <span className='ml-2 flex-1 rounded-md bg-obsidian/80 px-3 py-1 text-center text-[11px] text-muted'>
      timbercloud.com
    </span>
  </div>
);

export function LaunchHero() {
  const [active, setActive] = useState(0);
  // Once the visitor picks a slide themselves, stop advancing — taking control
  // shouldn't get overridden a few seconds later.
  const [locked, setLocked] = useState(false);
  const frameRef = useRef(null);

  const pick = useCallback((i) => {
    setActive(i);
    setLocked(true);
  }, []);

  useEffect(() => {
    if (locked) return undefined;
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return undefined;
    }

    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(
        () => setActive((i) => (i + 1) % SLIDES.length),
        CYCLE_MS,
      );
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };

    // Don't burn cycles while the reel is off-screen.
    const el = frameRef.current;
    let io = null;
    if (el && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? start() : stop()),
        { threshold: 0.25 },
      );
      io.observe(el);
    } else {
      start();
    }

    return () => {
      stop();
      if (io) io.disconnect();
    };
  }, [locked]);

  return (
    <section className='relative overflow-hidden px-6 pt-32 pb-16 md:pt-36 md:pb-24'>
      <div className='container relative z-10 mx-auto max-w-7xl'>
        {/* The reel gets the larger column — it is the proof that the headline
            is true, so it should out-weigh the copy rather than split evenly. */}
        <div className='grid items-center gap-12 lg:grid-cols-[1fr_1.35fr] lg:gap-14'>
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className='mb-6 inline-block rounded-full border border-jade-deep/60 bg-jade-deep/20 px-4 py-1.5 font-sans text-[11px] uppercase tracking-[0.25em] text-jade-soft'>
              Custom Manufacturing Commerce
            </span>

            <h1 className='font-display text-4xl font-extrabold leading-[1.05] text-white [text-wrap:balance] sm:text-5xl md:text-6xl'>
              TimberCloud <span className='text-jade-soft'>is live.</span>
            </h1>

            <p className='mt-6 max-w-xl text-lg leading-relaxed text-muted'>
              The order-to-shipment platform for custom manufacturers — built by
              a developer who grew up on the shop floor, running in production
              at a 60-year-old cabinet-door shop today.
            </p>

            <div className='mt-8 flex flex-wrap items-center gap-4'>
              <DemoCtaLink
                href='/demo'
                ctaLocation='launch_hero'
                className='inline-flex items-center gap-2 rounded-full bg-jade-cta px-7 py-3.5 font-semibold text-obsidian transition-colors hover:bg-jade-cta-deep'
              >
                Book a demo
                <span aria-hidden='true'>→</span>
              </DemoCtaLink>
              <Link
                href='#what-it-does'
                className='inline-flex items-center gap-2 rounded-full border border-jade-deep/60 px-7 py-3.5 font-semibold text-jade-soft transition-colors hover:border-jade-soft hover:text-white'
              >
                See it in action
                <span aria-hidden='true'>↓</span>
              </Link>
            </div>

            <p className='mt-8 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-line/60 pt-6 text-sm text-muted'>
              <span
                className='h-1.5 w-1.5 rounded-full bg-jade'
                aria-hidden='true'
              />
              Officially launching at{' '}
              <Link
                href='/iwf-2026'
                className='text-jade-soft underline underline-offset-4 hover:text-white'
              >
                {IWF.show}, {IWF.dates}
              </Link>
              {/* Separator kept in the same span as the booth so the two wrap
                  together — otherwise the "·" strands at the end of a line. */}
              <span className='whitespace-nowrap'>
                <span aria-hidden='true'>· </span>
                Booth {IWF.booth}, {IWF.hall}
              </span>
            </p>
          </motion.div>

          {/* Reel, framed as a browser window */}
          <motion.div
            ref={frameRef}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className='overflow-hidden rounded-xl border border-white/10 bg-onyx shadow-2xl'>
              {WINDOW_CHROME}
              <div className={`relative ${FRAME_ASPECT} w-full bg-obsidian`}>
                {SLIDES.map((slide, i) => (
                  <Image
                    key={slide.src}
                    src={slide.src}
                    alt={slide.alt}
                    width={slide.width}
                    height={slide.height}
                    priority={i === 0}
                    sizes='(min-width: 1024px) 52vw, 92vw'
                    className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 motion-reduce:transition-none ${
                      i === active ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className='mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3'>
              <p className='text-sm leading-relaxed text-muted'>
                {SLIDES[active].label}
              </p>

              {/* The BUTTON is the tap target (24x24, the WCAG 2.2 SC 2.5.8
                  floor); the inner span is just the visible dot. Keeping those
                  separate matters: the active dot scales, and if the target
                  scaled with it the selected dot would inflate and steal its
                  neighbour's target — making hit-area size depend on which
                  slide happens to be showing.

                  Sized in explicit px, NOT h-6/w-6: this app sets the root
                  font-size to 14px, so rem-based Tailwind sizes render at
                  87.5% and h-6 would be 21px. SC 2.5.8's 24px is an absolute
                  CSS pixel minimum and must not track the root font size. */}
              <div className='flex flex-none gap-1'>
                {SLIDES.map((slide, i) => (
                  <button
                    key={slide.src}
                    type='button'
                    onClick={() => pick(i)}
                    aria-label={slide.label}
                    aria-pressed={i === active}
                    className='group flex h-[24px] w-[24px] items-center justify-center'
                  >
                    <span
                      className={`h-2 w-2 rounded-full transition-[background-color,transform] duration-200 motion-reduce:transition-none ${
                        i === active
                          ? 'scale-[1.35] bg-jade-soft'
                          : 'bg-line group-hover:bg-muted'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default LaunchHero;
